/**
 * Topo Nomad Studio links.
 *
 * Every "Design yours now" anchor is rendered complete by
 * snippets/tn-studio-link.liquid, so the link works with JavaScript disabled.
 * This module adds the things Liquid cannot know:
 *
 *   - utm_* params from the current URL, plus fbp / fbc from the Meta pixel
 *     cookies, so Studio can pass attribution back to Shopify at checkout.
 *   - the variant the shopper has actually selected, kept in sync with the
 *     variant picker.
 *   - show/hide behaviour for the sticky CTA bars.
 *
 * Loaded from the snippet rather than the layout, so it is only fetched on
 * pages that have a Studio link on them.
 */
if (!window.__tnStudioLink) {
  window.__tnStudioLink = true;

  const LINK_SELECTOR = 'a[data-tn-studio-link]';

  /** @param {string} name */
  function readCookie(name) {
    const parts = document.cookie ? document.cookie.split('; ') : [];
    for (const part of parts) {
      const index = part.indexOf('=');
      if (index > 0 && part.slice(0, index) === name) {
        try {
          return decodeURIComponent(part.slice(index + 1));
        } catch {
          return part.slice(index + 1);
        }
      }
    }
    return '';
  }

  /**
   * Campaign + Meta click identifiers for the current page view.
   * Read fresh on each build so a link clicked after a client-side navigation
   * still carries the right campaign.
   * @returns {Record<string, string>}
   */
  function tracking() {
    /** @type {Record<string, string>} */
    const out = {};

    try {
      const search = new URLSearchParams(window.location.search);
      for (const [key, value] of search.entries()) {
        if (key.startsWith('utm_') && value) out[key] = value;
      }

      const fbp = readCookie('_fbp');
      if (fbp) out.fbp = fbp;

      // _fbc is only written once the pixel has seen an fbclid. On the very
      // first ad click the cookie can lag the URL, so build it the same way
      // the pixel does.
      const fbc = readCookie('_fbc');
      const fbclid = search.get('fbclid');
      if (fbc) {
        out.fbc = fbc;
      } else if (fbclid) {
        out.fbc = `fb.1.${Date.now()}.${fbclid}`;
      }
    } catch {
      // A malformed location should never cost us the link.
    }

    return out;
  }

  /**
   * Rebuild an anchor's href from its data attributes. Always built from the
   * base plus data, never from the current href, so repeated calls are safe.
   * @param {HTMLAnchorElement} anchor
   */
  function build(anchor) {
    const base = anchor.dataset.tnStudioBase;
    // Only absolute or root-relative bases. Anything else would resolve
    // against the shop origin and produce a dead link.
    if (!base || !(/^https?:\/\//i.test(base) || base.startsWith('/'))) return;

    try {
      const url = new URL(base, window.location.origin);
      const params = new URLSearchParams(anchor.dataset.tnStudioParams || '');

      const variantId = anchor.dataset.tnStudioVariantId;
      if (variantId) params.set('variant_id', variantId);

      for (const [key, value] of params.entries()) {
        url.searchParams.set(key, value);
      }
      for (const [key, value] of Object.entries(tracking())) {
        url.searchParams.set(key, value);
      }

      anchor.href = url.toString();
    } catch {
      // Leave the Liquid-rendered href in place.
    }
  }

  /** @param {ParentNode} [root] */
  function enrichAll(root = document) {
    root.querySelectorAll(LINK_SELECTOR).forEach((anchor) => {
      build(/** @type {HTMLAnchorElement} */ (anchor));
    });
  }

  // Cards arrive after load too — infinite scroll, recommendations, quick
  // views. Rebuilding on the way into the click covers all of them without
  // watching the DOM.
  document.addEventListener(
    'click',
    (event) => {
      const target = /** @type {Element | null} */ (event.target);
      const anchor = target?.closest?.(LINK_SELECTOR);
      if (anchor) build(/** @type {HTMLAnchorElement} */ (anchor));
    },
    { capture: true }
  );

  // The theme's variant picker re-renders the section and announces the new
  // variant. Keep every link for that product pointed at it.
  document.addEventListener('variant:update', (event) => {
    const detail = /** @type {any} */ (event).detail;
    const variant = detail?.resource;
    if (!variant?.id) return;

    const productId = detail?.data?.productId ? String(detail.data.productId) : '';

    document.querySelectorAll(LINK_SELECTOR).forEach((node) => {
      const anchor = /** @type {HTMLAnchorElement} */ (node);
      if (productId && anchor.dataset.tnStudioProductId !== productId) return;
      anchor.dataset.tnStudioVariantId = String(variant.id);
      build(anchor);
    });

    // Copy the price straight out of the re-rendered section markup rather
    // than formatting money in JavaScript.
    const source = detail?.data?.html?.querySelector?.('[data-tn-studio-price]');
    if (source) {
      document.querySelectorAll('[data-tn-studio-price]').forEach((node) => {
        node.textContent = source.textContent;
      });
    }
  });

  /**
   * Sticky bars hide while their anchor element is on screen, so the shopper
   * never sees two competing CTAs at once.
   */
  function initStickyBars() {
    document.querySelectorAll('[data-tn-studio-sticky]').forEach((node) => {
      const bar = /** @type {HTMLElement} */ (node);
      if (bar.dataset.tnStudioStickyReady === 'true') return;
      bar.dataset.tnStudioStickyReady = 'true';

      const selector = bar.dataset.tnStudioHideWhen;
      const watched = selector ? document.querySelector(selector) : null;

      if (!watched || typeof IntersectionObserver === 'undefined') {
        bar.classList.remove('is-hidden');
        return;
      }

      bar.classList.add('is-hidden');

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            // Hidden while the anchor is visible; also hidden until the
            // shopper has scrolled past it, so the bar never covers the hero.
            const above = entry.boundingClientRect.top < 0;
            bar.classList.toggle('is-hidden', entry.isIntersecting || !above);
          }
        },
        { rootMargin: '0px 0px -10% 0px' }
      );

      observer.observe(watched);
    });
  }

  function init() {
    enrichAll();
    initStickyBars();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Theme editor: sections are swapped in and out without a page load.
  document.addEventListener('shopify:section:load', () => init());
}
